import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  api,
  type AppEvent,
  type GraphqlExpense,
  type PullAccount,
} from "../api";
import { useAuth } from "../auth/AuthContext";
import { pullFromEvent, statusMeta } from "../components/AccountFeed";
import { EventDetailModal } from "../components/EventDetailModal";
import { PaginationBar, usePagination } from "../components/Pagination";
import { isElectronDesktop } from "../desktop";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { useLocalEventsQuery } from "../hooks/localEventsStore";

type TxRow = GraphqlExpense & {
  email: string;
  eventId: number;
  event: AppEvent;
};

const FEED_OPTS = { limit: 2000, includeHidden: false } as const;

function expensesOf(pull: PullAccount | null): GraphqlExpense[] {
  const rows = pull?.graphql?.expenses;
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => row && typeof row.id === "string" && row.id);
}

function formatMoney(qty?: string, ccy?: string): string {
  if (qty == null || qty === "") return "—";
  const n = Number(qty);
  const num = Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : qty;
  return ccy ? `${num} ${ccy}` : num;
}

function displayAmount(row: GraphqlExpense): string {
  if (row.purchasedAmount) return formatMoney(row.purchasedAmount, row.purchasedCurrency);
  if (row.originalAmount) return formatMoney(row.originalAmount, row.originalCurrency);
  return formatMoney(row.billingAmount, row.billingCurrency);
}

function displayBilling(row: GraphqlExpense): string {
  const billed = formatMoney(row.billingAmount, row.billingCurrency);
  const charged = displayAmount(row);
  return billed === charged ? "—" : billed;
}

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function receiptLabel(row: GraphqlExpense): string {
  if (row.hasUploadedReceipt) return row.receiptCount ? `uploaded (${row.receiptCount})` : "uploaded";
  if (row.hasAutoGeneratedReceipt) return "auto";
  if (row.receiptCount && row.receiptCount > 0) return String(row.receiptCount);
  return "—";
}

function pillClass(kind: string | undefined, map: Record<string, string>, fallback = "type-unknown"): string {
  const key = String(kind || "").toUpperCase();
  return `type-pill ${map[key] || fallback}`;
}

const PAYMENT_PILL: Record<string, string> = {
  CLEARED: "type-ok",
  PROCESSING: "type-pending",
  DECLINED: "type-urgent",
};

const STATUS_PILL: Record<string, string> = {
  APPROVED: "type-ok",
  SUBMITTED: "type-pending",
  CANCELED: "type-unknown",
  CANCELLED: "type-unknown",
};

function fieldCount(row: GraphqlExpense): number {
  return Object.values(row).filter((v) => v != null && v !== "").length;
}

export function TransactionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const desktop = isElectronDesktop();
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [httpSeed, setHttpSeed] = useState<AppEvent[]>([]);
  const [seedError, setSeedError] = useState<string | null>(null);

  const { status, events: liveEvents, error: liveError } = useLiveEvents({
    enabled: Boolean(user) && isAdmin,
  });
  const meta = statusMeta(status);
  const { events: localSeed } = useLocalEventsQuery(
    FEED_OPTS,
    Boolean(desktop && isAdmin)
  );

  useEffect(() => {
    if (!isAdmin || desktop) return;
    let cancelled = false;
    void api<{ logins: AppEvent[] }>("/api/data/logins?limit=500")
      .then((data) => {
        if (!cancelled) {
          setHttpSeed(data.logins || []);
          setSeedError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSeedError(err instanceof Error ? err.message : "Failed to load transactions");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop]);

  const seed = desktop ? localSeed : httpSeed;

  const merged = useMemo(() => {
    const map = new Map<number, AppEvent>();
    for (const e of seed) map.set(e.id, e);
    for (const e of liveEvents) map.set(e.id, e);
    return [...map.values()];
  }, [seed, liveEvents]);

  const rows = useMemo(() => {
    const byId = new Map<string, TxRow>();
    for (const ev of merged) {
      const pull = pullFromEvent(ev);
      const email = pull?.email || ev.resourceKey || "";
      for (const exp of expensesOf(pull)) {
        const next: TxRow = { ...exp, email, eventId: ev.id, event: ev };
        const prev = byId.get(exp.id);
        if (!prev) {
          byId.set(exp.id, next);
          continue;
        }
        const richer = fieldCount(next) >= fieldCount(prev) ? next : prev;
        const emails = [...new Set([prev.email, next.email].filter(Boolean))];
        byId.set(exp.id, { ...richer, email: emails.join(", ") });
      }
    }
    const q = filter.trim().toLowerCase();
    return [...byId.values()]
      .filter((row) => {
        if (!q) return true;
        const hay = [
          row.email,
          row.merchant,
          row.category,
          row.employee,
          row.status,
          row.paymentStatus,
          row.type,
          row.cardLast4,
          row.limitName,
          row.purchasedAmount,
          row.billingAmount,
          row.originalAmount,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ta = Date.parse(a.purchasedAt || "") || 0;
        const tb = Date.parse(b.purchasedAt || "") || 0;
        return tb - ta || b.eventId - a.eventId;
      });
  }, [merged, filter]);

  const {
    page,
    setPage,
    pageCount,
    pageSize,
    total,
    slice: pageRows,
  } = usePagination(rows, 50, filter);

  const selectedLive = selected
    ? merged.find((e) => e.id === selected.id) || selected
    : null;

  if (user && !isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <h1>Transactions</h1>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter merchant, email, last4…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className="muted tiny">{total} captured</span>
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>
      </header>

      {(seedError || liveError) && (
        <div className="banner error">{seedError || liveError}</div>
      )}

      <div className="surface logins-table-wrap">
        <table className="logins-table">
          <thead>
            <tr>
              <th>Purchased</th>
              <th>Merchant</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Billing</th>
              <th>Payment</th>
              <th>Status</th>
              <th>Type</th>
              <th>Employee</th>
              <th>Card</th>
              <th>Limit</th>
              <th>Receipt</th>
              <th>Account</th>
            </tr>
          </thead>
          <tbody>
            {total === 0 && (
              <tr>
                <td colSpan={13} className="muted">
                  No transactions yet.
                </td>
              </tr>
            )}
            {pageRows.map((row) => (
              <tr
                key={row.id}
                className={`logins-row${
                  String(row.paymentStatus || "").toUpperCase() === "DECLINED"
                    ? " is-declined"
                    : ""
                }`}
                onClick={() => setSelected(row.event)}
              >
                <td className="mono tiny" title={row.purchasedAt}>
                  {formatWhen(row.purchasedAt)}
                </td>
                <td className="truncate" title={row.merchant}>
                  {row.merchant || "—"}
                </td>
                <td className="truncate muted tiny" title={row.category}>
                  {row.category || "—"}
                </td>
                <td className="mono tiny num" title={displayAmount(row)}>
                  {displayAmount(row)}
                </td>
                <td className="mono tiny num" title={displayBilling(row)}>
                  {displayBilling(row)}
                </td>
                <td>
                  <span className={pillClass(row.paymentStatus, PAYMENT_PILL)}>
                    {row.paymentStatus || "—"}
                  </span>
                </td>
                <td>
                  <span className={pillClass(row.status, STATUS_PILL)}>
                    {row.status || "—"}
                  </span>
                </td>
                <td className="tiny">
                  {row.reimbursementType || row.type || "—"}
                </td>
                <td className="truncate" title={row.employee}>
                  {row.employee || "—"}
                </td>
                <td className="mono tiny">{row.cardLast4 ? `•••• ${row.cardLast4}` : "—"}</td>
                <td className="truncate muted tiny" title={row.limitName || row.spendLimitName}>
                  {row.limitName || row.spendLimitName || "—"}
                </td>
                <td className="tiny muted">{receiptLabel(row)}</td>
                <td className="truncate" title={row.email}>
                  {row.email || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar
          page={page}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          className="pagination-bar-table"
        />
      </div>

      {selectedLive && (
        <EventDetailModal event={selectedLive} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
